import { useState, useEffect, useCallback } from 'react';
import { icCidService } from '../../services/icCids/ICcIdService';
import type { FastChipMovementEventDto } from '../../types/dtos';

function movementOccurredAtIso(ev: FastChipMovementEventDto): string {
  return ev.occurredAt ?? ev.occurredAtUtc ?? '';
}

function sortByOccurredAtAsc(items: FastChipMovementEventDto[]): FastChipMovementEventDto[] {
  return [...items].sort((a, b) => {
    const ta = new Date(movementOccurredAtIso(a)).getTime();
    const tb = new Date(movementOccurredAtIso(b)).getTime();
    if (Number.isNaN(ta) || Number.isNaN(tb)) return 0;
    return ta - tb;
  });
}

/**
 * Carrega histórico append-only de movimentos FAST para um controlo ICCID (modal admin).
 * Endpoint: GET `/icCids/{id}/movements` (ver `ICcIdService.getFastChipMovementHistory`).
 */
export function useFastChipMovementHistory(icCidControlId: string | null, open: boolean) {
  const [events, setEvents] = useState<FastChipMovementEventDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!icCidControlId || !open) return;
    setLoading(true);
    setError(null);
    try {
      const list = await icCidService.getFastChipMovementHistory(icCidControlId);
      setEvents(sortByOccurredAtAsc(list));
    } catch (e: unknown) {
      const msg =
        e instanceof Error ? e.message : 'Não foi possível carregar o histórico de movimentos.';
      setError(msg);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [icCidControlId, open]);

  useEffect(() => {
    if (!open || !icCidControlId) {
      setEvents([]);
      setError(null);
      return;
    }
    void load();
  }, [open, icCidControlId, load]);

  return { events, loading, error, reload: load };
}
